import { useDroppable } from '@dnd-kit/core';
import { cn, getPhaseColor } from '../lib/utils';
import type { Column } from '../lib/api';
import type { DropState } from '../lib/move-rules';
import type { ReactNode } from 'react';

interface Props {
  column: Column;
  cardCount: number;
  isOver: boolean;
  dropState?: DropState;
  isDragging?: boolean;
  isAgentPhase?: boolean;
  isDisabled?: boolean;
  phaseColor?: string;
  onEmptyClick?: () => void;
  children: ReactNode;
}

export function KanbanColumn({ column, cardCount, isOver, dropState, isDragging, isAgentPhase, isDisabled, phaseColor, onEmptyClick, children }: Props) {
  const { setNodeRef } = useDroppable({ id: column.id });

  // Suppress empty-click handler during drag to prevent accidental modal opens
  const activeEmptyClick = isDragging ? undefined : onEmptyClick;

  // Compute visual ring/background based on drag state
  const dragClasses = (() => {
    if (!isDragging || !dropState) return '';

    if (dropState === 'same-column') return '';

    if (dropState === 'valid') {
      return isOver
        ? 'ring-2 ring-green-500/40 bg-green-500/5'
        : 'ring-1 ring-green-500/20';
    }

    // blocked
    return isOver
      ? 'ring-2 ring-red-500/40 bg-red-500/5 cursor-not-allowed'
      : 'ring-1 ring-red-500/15';
  })();

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col rounded-xl transition-all duration-200',
        isDisabled && 'opacity-60',
        dragClasses
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 whitespace-nowrap px-3 py-3">
        <span className={cn('h-2 w-2 rounded-full', getPhaseColor(phaseColor).dot)} />
        <h3 className={cn('text-xs font-semibold uppercase tracking-wider text-gray-400', isDisabled && 'line-through')}>
          {column.name}
        </h3>
        {isAgentPhase && (
          <span className="rounded-md bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-400" title="Cards in this phase are handled by an agent and advance automatically">
            Auto
          </span>
        )}
        {isDisabled && (
          <span className="rounded-md bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-400" title="This phase is disabled — cards skip to the next phase">
            Disabled
          </span>
        )}
        <span className="ml-auto rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-400">
          {cardCount}
        </span>
      </div>
      {isDisabled && (
        <div className="flex items-center gap-1.5 px-3 pb-2 text-[10px] text-gray-500">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
          <span>Skips to next</span>
        </div>
      )}

      {/* Cards */}
      <div className="flex gap-2.5 overflow-x-auto px-2 pb-2">
        {children}
        {cardCount === 0 && (
          <div
            onClick={activeEmptyClick}
            onKeyDown={activeEmptyClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activeEmptyClick(); } } : undefined}
            role={activeEmptyClick ? 'button' : undefined}
            tabIndex={activeEmptyClick ? 0 : undefined}
            aria-label={activeEmptyClick ? `Add a card to ${column.name}` : undefined}
            data-testid={activeEmptyClick ? 'empty-column-add-card' : undefined}
            className={cn(
              'flex w-48 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-800 text-xs text-gray-600',
              activeEmptyClick ? 'h-20 cursor-pointer hover:border-gray-600 hover:bg-gray-800/40 hover:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:border-gray-600 transition-colors' : 'h-20'
            )}
          >
            {activeEmptyClick ? '+ Add a card' : 'No cards'}
          </div>
        )}
      </div>
    </div>
  );
}
